import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import Main, { ComponentName } from '@/components/Main';
import getGlobalStyleSheets from '@/bundles/helpers/getGlobalStyleSheets';
import '../overrideStyles.css';
import { store } from '../redux/store';
import MockScenarioHelperSlot from '@/mocks/components/MockScenarioHelperSlot';
import shouldRequestBeBypassed from './helpers/shouldRequestBeBypassed';
import enableMocking from './helpers/enableMocking';
import getCurrentClientCode from './helpers/getCurrentClientCode';
import getCurrentComponentName from './helpers/getCurrentComponentName';
import setupGlobalStyleObserver from '@/bundles/helpers/setupGlobalStyleObserver';
import mockRequestsBypassList from './constants';
import parseHeaderModel from '@/components/global/Header/utils/parseHeaderModel';
import parseFooterModel from '@/components/global/Footer/utils/parseFooterModel';

const roots = new Map();

const domElements = document.querySelectorAll('[id^="react-container-global"]');

enableMocking({
  shouldRequestBeBypassed,
  mockRequestsBypassList,
})
  .then(() => {
    domElements.forEach((domElement) => {
      if (!roots.has(domElement.id)) {
        const { shadowRoot } = domElement;
        const reactRoot = shadowRoot?.getElementById('react-root');

        const translations =
          domElement.getAttribute('data-translations') ?? undefined;
        const language = domElement.getAttribute('data-language') ?? undefined;
        const headerData = domElement.getAttribute('data-header-data') ?? undefined;
        const headerDataParsed = parseHeaderModel(headerData);
        const searchInputMaxLength =
          domElement.getAttribute('data-search-input-max-length') ?? undefined;
        const footerData = domElement.getAttribute('data-footer-data') ?? undefined;
        const footerDataParsed = parseFooterModel(footerData);

        const clientCode = getCurrentClientCode(domElement);
        const componentName = getCurrentComponentName({
          element: domElement,
          ComponentEnum: ComponentName,
          defaultComponent: ComponentName.NotFound,
        });

        setupGlobalStyleObserver({
          shadowRoot,
          getGlobalStyleSheets,
        });

        if (reactRoot) {
          const root = createRoot(reactRoot);
          root.render(
            <StrictMode>
              <Provider store={store}>
                <MockScenarioHelperSlot />
                <Main
                  componentName={componentName}
                  translations={translations}
                  language={language}
                  clientCode={clientCode}
                  headerData={headerDataParsed}
                  footerData={footerDataParsed}
                  searchInputMaxLength={searchInputMaxLength}
                />
              </Provider>
            </StrictMode>,
          );
          roots.set(domElement.id, root);
        }
        domElement.classList.add('global-react-loaded');
      }
    });
  })
  .catch((error) => console.error(error));
